import { CONTEXT_RIGHT_UPDATE_LEGACY, ContextIdRights } from './context-rights-picker.component';

/**
 * What the active context may do with a document.
 *
 * - `owner`  the document belongs to the active context (created here) → edit everything
 * - `update` shared into the active context with the `update` right → metadata only, no geodata
 * - `view`   shared read-only → form is shown but fully disabled
 */
export type ContextAccess = 'owner' | 'update' | 'view';

interface SharedDoc {
  contextId?: string;
  contextIds?: string[];
  contextIdRights?: ContextIdRights;
}

export function resolveContextAccess(
  doc: SharedDoc | null | undefined,
  activeContextId: string | null | undefined,
  subContextIds?: string[] | null,
): ContextAccess {
  if (!doc || !activeContextId || !doc.contextId) return 'owner';
  // Created in this context → full control.
  if (doc.contextId === activeContextId) return 'owner';

  if (Array.isArray(subContextIds) && subContextIds.includes(doc.contextId)) return 'owner';

  // Shared into this context → the stored right decides; `view` is the fallback.
  const shared = Array.isArray(doc.contextIds) && doc.contextIds.includes(activeContextId);
  if (shared) {
    const right = doc.contextIdRights?.[activeContextId] as string | undefined;
    // `edit` is what earlier versions wrote for the same level — documents saved
    // before the rename must not silently drop to read-only.
    return right === 'update' || right === CONTEXT_RIGHT_UPDATE_LEGACY ? 'update' : 'view';
  }

  // Neither owner, nor a sub-context's document, nor shared in.
  return 'view';
}

export function isReadOnly(access: ContextAccess): boolean {
  return access === 'view';
}

/**
 * Convenience: geodata (geometry, outlines, coordinates) must stay untouched.
 * True for both `view` and the restricted `edit` mode.
 */
export function isGeoLocked(access: ContextAccess): boolean {
  return access !== 'owner';
}

export function coordKey(value: any): string | null {
  if (value == null) return null;
  const lat = value.latitude ?? value._lat ?? value._latitude ?? value.lat;
  const lng = value.longitude ?? value._long ?? value._longitude ?? value.lng;
  if (lat == null || lng == null) return null;
  return `${Number(lat).toFixed(7)},${Number(lng).toFixed(7)}`;
}

export function changedGeoFields<T extends Record<string, any>>(
  original: T | null | undefined,
  next: T | null | undefined,
  fields: (keyof T)[],
): string[] {
  const norm = (v: any) => JSON.stringify(v ?? null);
  return fields
    .filter(f => norm(original?.[f]) !== norm(next?.[f]))
    .map(f => String(f));
}